import { Link, useNavigate } from '@tanstack/react-router';
import type { MouseEvent, ReactNode } from 'react';

import { getToken } from '~entities/studios';
import { getCurrentStudio } from '~entities/studios/model/studio-store';
import { getCurrentUser } from '~entities/users/model/user-store';
import {
  buildCrmSessionBreakoutHref,
  buildStudentDetailsReturnTo,
  openStandalonePortalWindow,
} from '~shared/utils/crm-embed-open-standalone';
import { isEmbeddedInCrmIframe } from '~shared/utils/is-embedded-in-crm-iframe';

type StudentDetailsCrmLinkProps = {
  studentId: string;
  className?: string;
  children: ReactNode;
};

function breakoutPayload() {
  const token = getToken();
  if (!token) {
    return null;
  }
  return {
    token,
    user: getCurrentUser(),
    studio: getCurrentStudio(),
  };
}

/**
 * ENR-4102: student detail links inside the CRM iframe.
 *
 * Chrome partitions iframe localStorage, so a normal "Open in new tab" loads
 * top-level Portal under whatever studio was last used standalone (often a
 * different currency / empty records). When embedded:
 * - plain click stays in the iframe
 * - modified / middle click opens breakout with opener handoff
 * - right-click open uses the breakout href (no silent wrong-studio page)
 */
export function StudentDetailsCrmLink({
  studentId,
  className,
  children,
}: StudentDetailsCrmLinkProps) {
  const navigate = useNavigate();
  const embedded = isEmbeddedInCrmIframe();
  const locationId = getCurrentStudio()?.locationId;
  const returnTo = buildStudentDetailsReturnTo(studentId, locationId);

  if (!embedded) {
    return (
      <Link
        to='/students/$studentId/details'
        params={{ studentId }}
        search={locationId ? { id: locationId } : undefined}
        className={className}
      >
        {children}
      </Link>
    );
  }

  const href = buildCrmSessionBreakoutHref({
    origin: window.location.origin,
    pathname: window.location.pathname,
    search: window.location.search,
    hash: '',
    returnTo,
    locationId,
  });

  const openWithCrmSession = () => {
    const child = openStandalonePortalWindow({
      loc: {
        origin: window.location.origin,
        pathname: window.location.pathname,
        search: window.location.search,
        hash: '',
        returnTo,
        locationId,
      },
      getPayload: breakoutPayload,
    });
    if (!child) {
      window.alert(
        'Your browser blocked the new window. Allow pop-ups for Studio Portal and try again.',
      );
    }
  };

  const onClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (event.defaultPrevented) {
      return;
    }
    if (event.button !== 0) {
      return;
    }
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      event.preventDefault();
      openWithCrmSession();
      return;
    }
    // Stay in the CRM iframe for a normal click.
    event.preventDefault();
    void navigate({
      to: '/students/$studentId/details',
      params: { studentId },
      search: locationId ? { id: locationId } : undefined,
    });
  };

  const onAuxClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (event.button === 1) {
      event.preventDefault();
      openWithCrmSession();
    }
  };

  return (
    <a
      href={href}
      className={className}
      onClick={onClick}
      onAuxClick={onAuxClick}
    >
      {children}
    </a>
  );
}
